import type { ReactNode } from "react";
import FIBenchmarkIcon from "Assets/Img/FIBenchmarkIcon";
import ListIcon from "Assets/Img/ListIcon";
import DocIcon from "Assets/Img/DocIcon";
import LineWaiverIcon from "Assets/Img/LineWaiverIcon";
import ProgressIcon from "Assets/Img/ProgressIcon";
import ProfileIcon from "Assets/Img/ProfileIcon";
import InspectionIcon from "Assets/Img/InspectionIcon";
import HistoryIcon from "Assets/Img/HistoryIcon";
import TickIcon from "Assets/Img/TickIcon";

export interface MoneyField {
  amount: number;
  currency?: { symbol?: string };
}

export interface G702Data {
  originalContractSum?: MoneyField;
  sovUpdate?: MoneyField;
  netChangeByChangeOrders?: MoneyField;
  contractSumToDate?: MoneyField;
  totalCompletedAndStored?: MoneyField;
  retainageRate?: string;
  totalRetainage?: MoneyField;
  totalEarnedLessRetainage?: MoneyField;
  lessPreviousCertificates?: MoneyField;
  currentPaymentDue?: MoneyField;
  balanceToFinish?: MoneyField;
}

export interface DrawListItem {
  id: string;
  drawNumber: number;
  status?: string;
  toDate?: string;
  requestedAmount?: MoneyField;
  approvedAmount?: MoneyField;
}

export interface DrawInfo {
  id: string;
  drawNumber: number;
  status?: string;
  drawDate?: string | null;
  toDate?: string;
  requestedAmount?: MoneyField;
  approvedAmount?: MoneyField | null;
  submittedBy?: {
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

export interface DrawG703Row {
  aiaItemId?: string;
  aiaItem?: {
    id?: string;
    itemCode?: string | number;
    itemName?: string;
    itemLabel?: string;
  };
  aiaItemCode?: string | number;
  description?: string;
  displayOrder?: number;
  sovValue?: MoneyField;
  previousBilled?: MoneyField;
  currentApplication?: MoneyField;
  currentStoredMaterial?: MoneyField;
  totalCompletedAndStored?: MoneyField;
  percentageComplete?: number;
  retainageAmount?: MoneyField;
  balanceToFinish?: MoneyField;
  flagged?: boolean;
  flagMessage?: string | null;
  cscEstimation?: string | null;
}

export interface DrawInvoiceInfo {
  documentId?: string;
  fileName?: string;
  url?: string;
  status?: string;
  uploadedAt?: string;
}

export interface DrawLienWaiverItem {
  waiverDocument: {
    url?: string;
  };
  id: string;
  lienItemId?: string;
  status?: string;
  companyName?: string;
  signerName?: string;
  title?: string | null;
  subContractor?: {
    id?: string;
    companyName?: string;
    email?: string;
    phone?: string;
  } | null;
  aiaItem?: {
    id?: string;
    itemCode?: string | number;
    itemName?: string;
    itemLabel?: string;
  };
  description?: string | null;
  totalAmount?: MoneyField;
  retainageAmount?: MoneyField;
  paidAmount?: MoneyField | null;
  remainingAmount?: MoneyField | null;
  isFinalPayment?: boolean;
  isLienWaiverRequired?: boolean;
  signedAt?: string | null;
  invitedAt?: string | null;
  inviteCount?: number;
  invoice?: DrawInvoiceInfo | null;
}

export interface DrawSignatureInfo {
  name?: string;
  designation?: string;
  company?: string;
  signedAt?: string;
  signatureUrl?: string;
}

export interface DrawSignatures {
  owner?: DrawSignatureInfo;
  gc?: DrawSignatureInfo;
}

export interface DrawApprovalInfo {
  status?: string;
  isApproved?: boolean | null;
  approvedBy?: {
    id?: string;
    name?: string;
    title?: string | null;
    company?: string;
    bankName?: string;
    branchName?: string;
    display?: string;
  } | null;
  approvalDate?: string | null;
  amountCertified?: MoneyField | null;
  fundsDisbursed?: {
    date?: string;
    amount?: MoneyField;
  } | null;
  approvalDocument?: {
    id?: string;
    fileName?: string;
    url?: string;
    type?: string;
  };

  cscRecommendation?: {
    reportId?: string;
    reportNumber?: number;
    reportDate?: string;
    recommendation?: string;
    text?: string;
    flagCount?: number;
  } | null;
  retainage?: { thisDraw?: MoneyField; cumulative?: MoneyField; rate?: string };
  bankNotes?: string | null;
}
export interface GcProgressItem {
  aiaItemId?: string;
  aiaItemCode?: string | number;
  description?: string;
  status?: string;
  note?: string | null;
}

export interface DrawDetail {
  retainage: import("react/jsx-runtime").JSX.Element;
  cumulative: any;
  draw: DrawInfo;
  summary?: G702Data;
  lineItems: { retainageRate?: string; rows: DrawG703Row[] };
  lienWaivers: { signed: number; total: number; items: DrawLienWaiverItem[] };
  gcProgress: GcProgressItem[];
  signatures?: DrawSignatures;
  approval?: DrawApprovalInfo;
}

const GC_PROGRESS_STATUS_MAP: Record<string, { label: string; cls: string }> = {
  not_started: { label: "Not Started", cls: "notStarted" },
  in_progress: { label: "In Progress", cls: "inProgress" },
  completed: { label: "Completed", cls: "nearingComplete" },
  nearing_complete: { label: "Nearing Comp.", cls: "nearingComplete" },
};

export const formatGcProgressStatus = (
  status?: string,
): { label: string; cls: string } => {
  if (!status) return { label: "—", cls: "" };
  return (
    GC_PROGRESS_STATUS_MAP[status] ?? {
      label: status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      cls: "",
    }
  );
};

export interface GcProgressDisplayItem {
  aiaItemId?: string;
  itemCode: string | number;
  description: string;
  status?: string;
  note?: string | null;
}

export const buildGcProgressDisplayItems = (
  drawDetail: DrawDetail | null,
): GcProgressDisplayItem[] =>
  (drawDetail?.gcProgress ?? []).map((item) => ({
    aiaItemId: item.aiaItemId,
    itemCode: item.aiaItemCode ?? "—",
    description: item.description ?? "—",
    status: item.status,
    note: item.note,
  }));

export interface DrawInspection {
  id: string;
  drawId: string;
  inspectionNumber: number;
  inspectorName?: string;
  cscEstimation?: string;
  inspectionDate?: string;
  isPublished?: boolean;
  publishedAt?: string;
  note?: { flags?: string; general?: string; offsite?: string };
}

export interface DrawInspectionLineItem {
  id: string;
  drawLienItemId: string;
  cscEstimation?: string;
  isFlagged?: boolean;
  note?: { flags?: string; general?: string; offsite?: string };
}

// Shape unconfirmed — empty in every sample response seen so far; assumed
// flat like DrawLienItem.invoice rather than nested, pending a populated sample.
export interface DrawInspectionPhoto {
  id: string;
  url?: string;
  fileName?: string;
}

export interface DrawInspectionData {
  inspection: DrawInspection | null;
  flagCount: number;
  lineItems: DrawInspectionLineItem[];
  photos: DrawInspectionPhoto[];
}

export interface DrawTimelineEvent {
  id: string;
  activityType: string;
  title: string;
  description?: string;
  createdAt: string;
  actor?: { userId?: string; userRole?: string; name?: string };
}

export type AiaTabId =
  | "summary"
  | "g703"
  | "invoices"
  | "waivers"
  | "progress"
  | "signatures"
  | "inspection"
  | "timeline"
  | "approval";

interface AiaTabDef {
  id: AiaTabId;
  icon: ReactNode;
  label: string;
}

// Static tab definitions, shared by whatever renders the Draw History /
// AIA Billing Summary navigation (currently the "Draw History" sidebar subItems).
const AIA_TAB_DEFS: AiaTabDef[] = [
  { id: "summary", icon: <FIBenchmarkIcon />, label: "Summary" },
  { id: "g703", icon: <ListIcon />, label: "G703 Line Items" },
  { id: "invoices", icon: <DocIcon />, label: "Invoices" },
  { id: "waivers", icon: <LineWaiverIcon />, label: "Lien Waivers" },
  { id: "progress", icon: <ProgressIcon />, label: "GC Progress" },
  { id: "signatures", icon: <ProfileIcon />, label: "Signatures" },
  { id: "inspection", icon: <InspectionIcon />, label: "Inspection" },
  { id: "timeline", icon: <HistoryIcon />, label: "Timeline" },
  { id: "approval", icon: <TickIcon />, label: "Approval" },
];

export interface AiaTabSubItem {
  id: string;
  label: string;
  icon: ReactNode;
  badge?: { text: string; variant?: string };
}

// Builds the sidebar subItems for the Draw History / AIA Billing Summary tabs,
// prefixing each id (e.g. "draws-summary") so it stays unique in the page-wide
// activeSection namespace. invoiceCount/waiverProgress feed the two live badges.
// icon is kept as its own field (not baked into label) so Sidebar can show it
// on its own — e.g. icon-only on mobile.
export const buildAiaTabSubItems = (
  idPrefix: string,
  invoiceCount: number,
  waiverProgress: string,
): AiaTabSubItem[] =>
  AIA_TAB_DEFS.map((tab) => {
    const badgeText =
      tab.id === "invoices"
        ? invoiceCount > 0
          ? String(invoiceCount)
          : ""
        : tab.id === "waivers"
          ? waiverProgress
          : "";
    return {
      id: `${idPrefix}-${tab.id}`,
      label: tab.label,
      icon: tab.icon,
      badge: badgeText ? { text: badgeText, variant: "view" } : undefined,
    };
  });

export const PENDING_DRAW_STATUSES = [
  "submitted",
  "inspection_pending",
  "owner_signature_pending",
];

export const fmtMoney = (field?: MoneyField): string => {
  if (!field || field.amount == null) return "—";
  const symbol = field.currency?.symbol ?? "$";
  return `${symbol}${field.amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};
